import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useRef, useState } from "react";
import { closeModal, Modal, openModal, TextInput } from "risa-oasis-ui_v2";
import { ModalId } from "../../../../../../../shared/enums/modalId";

interface AddValueModalProps {
  isOpen: boolean;
  fieldKey: string;
  fieldTitle: string;
  existingValues: string[];
  onClose: () => void;
  onSave: (values: string[]) => void;
}

const AddValueModal = ({
  isOpen,
  fieldKey,
  fieldTitle,
  existingValues,
  onClose,
  onSave,
}: AddValueModalProps) => {
  const [inputValue, setInputValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const isModalOpenRef = useRef(false);

  const dialogId = `${ModalId.AUTHORIZATION_LETTER_ADD_VALUE_MODAL}-${fieldKey}`;

  useEffect(() => {
    if (isOpen && !isModalOpenRef.current) {
      openModal(dialogId);
      isModalOpenRef.current = true;
    } else if (!isOpen && isModalOpenRef.current) {
      closeModal(dialogId);
      isModalOpenRef.current = false;
      setInputValue("");
      setIsSaving(false);
    }
  }, [isOpen, dialogId]);

  useEffect(() => {
    return () => {
      if (isModalOpenRef.current) {
        try {
          closeModal(dialogId);
        } catch (error) {
          console.error("Error closing modal:", error);
        }
      }
    };
  }, [dialogId]);

  const handleSave = () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) return;

    if (existingValues.includes(trimmedValue)) {
      return;
    }

    setIsSaving(true);
    onSave([...existingValues, trimmedValue]);
    setInputValue("");
    setIsSaving(false);
    toast.success(
      `The value "${trimmedValue}" has been added to ${fieldTitle.toLowerCase()} successfully!`,
    );
    onClose();
  };

  const isDuplicate =
    inputValue.trim() && existingValues.includes(inputValue.trim());

  return (
    <Modal
      dialogId={dialogId}
      onSave={handleSave}
      title={`Add ${fieldTitle}`}
      saveButtonText={isSaving ? "Adding..." : "Add"}
      cancelText="Cancel"
      disableSave={isSaving || !inputValue.trim() || !!isDuplicate}
      onClose={onClose}
    >
      <div className="flex flex-col gap-3">
        <TextInput
          id="value-input"
          label={`Enter ${fieldTitle.toLowerCase()}`}
          required
          onChange={(data) => setInputValue(data.value)}
          error={isDuplicate ? "This value already exists" : ""}
          resetField={inputValue === ""}
          defaultValue={inputValue}
          onKeyDown={(data, event) => {
            if (event.key === "Enter" && inputValue.trim() && !isDuplicate) {
              handleSave();
            }
          }}
        />
      </div>
    </Modal>
  );
};

export default AddValueModal;
